import useDelete from "./useDelete";
import useTrafficking from "../features/useTrafficking";
import useGCMSync from "../features/useGCMSync";
import useNewPlacement from "../features/useNewPlacement";
import usePrismaGCMSync from "../features/usePrismaGCMSync";
import usePrismaPush from "../features/usePrismaPush";
import { useMemo } from "react";
import useDuplicate from "./useDuplicate";
import useMultiply from "./useMultiply";
import useFindReplace from "./useFindReplace";
import useEdit from "./useEdit";

export default function useActions({
	// this comment exists to prevent eslint from making this unreadable.
	updateCounts,
	auditLevels,
	metaData,
	placementAPI,
	fetchMetaData,
	campaignJiraTicketExists,
	selectedRows,
	setSelectedRows,
	config,
	gcmStatus,
	prismaAPIEnabled,
	renderedData,
	setRenderedData,
	dataTableApi,
	refresh
}) {
	const [TraffickingTrigger, TraffickingModal, { clientTMEnabled, campaignTMEnabled, TMEnabled }] = useTrafficking({
		campaignMetaData: metaData,
		campaignJiraTicketExists,
		selectedRows,
		setSelectedRows,
		gcmEnabled: gcmStatus && metaData?.PlatformID == 1,
		refresh,
		dataTableApi
	});

	const [GCMTrigger, GCMModal] = useGCMSync({
		selectedRows,
		gcmEnabled: gcmStatus && metaData?.PlatformID == 1,
		dataTableApi,
		refresh,
		metaData
	});

	const [PrismaGCMSyncTrigger, PrismaGCMSyncModal, prismaGCMSyncTriggerHidden] = usePrismaGCMSync({
		campaignMetaData: metaData,
		TMEnabled,
		dataTableApi
	});

	const [NewPlacementTrigger, NewPlacementModal] = useNewPlacement({
		config,
		campaignMetaData: metaData,
		updateCounts,
		updateMetaData: fetchMetaData,
		auditLevelData: auditLevels,
		auditLevelID: auditLevels?.find(({ AuditLevelName }) => AuditLevelName == "Placement")?.AuditLevelID,
		dataTableApi,
		refresh
	});

	const [EditTrigger, EditModal] = useEdit({
		config,
		campaignMetaData: metaData,
		selectedRows,
		updateCounts,
		updateMetaData: fetchMetaData,
		auditLevelData: auditLevels,
		auditLevelID: auditLevels?.find(({ AuditLevelName }) => AuditLevelName == "Placement")?.AuditLevelID,
		refresh,
		dataTableApi
	});

	const [DeleteTrigger, DeleteModal] = useDelete({
		auditLevelData: auditLevels,
		campaignMetaData: metaData,
		selectedRows,
		placementAPI,
		updateCounts,
		updateMetaData: fetchMetaData,
		config,
		dataTableApi,
		refresh,
		prismaAPIEnabled
	});

	const [PrismaPushTrigger, PrismaPushModal] = usePrismaPush({
		campaignMetaData: metaData,
		selectedRows,
		setSelectedRows,
		auditLevelData: auditLevels,
		prismaAPIEnabled,
		dataTableApi,
		refresh
	});

	const [DuplicateTrigger, DuplicateModal] = useDuplicate({
		config,
		selectedRows,
		renderedData,
		dataTableApi
	});

	const [MultiplyTrigger, MultiplyModal] = useMultiply({
		config,
		selectedRows,
		renderedData,
		setRenderedData,
		dataTableApi
	});

	const [FindReplaceTrigger, FindReplaceModal, { displayInMoreMenu: displayFindReplaceModalInMoreMenu }] = useFindReplace({
		dataTableApi,
		renderedData,
		config,
		TMEnabled,
		campaignMetaData: metaData,
		setRenderedData
	});

	return useMemo(() => {
		const actions = [
			// this comment exists to prevent eslint from making this unreadable.
			<DuplicateTrigger />,
			<MultiplyTrigger />,
			<TraffickingTrigger />,
			<PrismaPushTrigger />,
			<GCMTrigger />,
			<EditTrigger />,
			<DeleteTrigger />
		];

		const defaultModeActions = [<NewPlacementTrigger />];
		const defaultModeMoreMenuActions = [];

		if (!prismaGCMSyncTriggerHidden) {
			defaultModeMoreMenuActions.push(<PrismaGCMSyncTrigger />);
		}

		if (displayFindReplaceModalInMoreMenu) {
			defaultModeMoreMenuActions.push(<FindReplaceTrigger />);
		} else {
			defaultModeActions.push(<FindReplaceTrigger />);
		}

		return {
			actionBarProps: { actions, defaultModeActions, defaultModeMoreMenuActions },
			modalComponents: [
				// this comment exists to prevent eslint from making this unreadable.
				TraffickingModal,
				GCMModal,
				NewPlacementModal,
				PrismaGCMSyncModal,
				EditModal,
				DeleteModal,
				PrismaPushModal,
				DuplicateModal,
				MultiplyModal,
				FindReplaceModal
			],
			traffickingProps: {
				clientTMEnabled,
				campaignTMEnabled,
				TMEnabled
			}
		};
	}, [
		// this comment exists to prevent eslint from making this unreadable.
		TraffickingTrigger,
		GCMTrigger,
		DeleteTrigger,
		PrismaPushTrigger,
		NewPlacementTrigger,
		PrismaGCMSyncTrigger,
		EditTrigger,
		TraffickingModal,
		GCMModal,
		NewPlacementModal,
		PrismaGCMSyncModal,
		EditModal,
		DeleteModal,
		PrismaPushModal,
		prismaGCMSyncTriggerHidden,
		displayFindReplaceModalInMoreMenu,
		selectedRows
	]);
}
